import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { Howl, Howler } from "howler";
import { GAME_SOUNDS, SOUND_NAME } from "../../sounds";
import { soundManager } from "../../SoundManager";
import StorageManager from "../../utils/StorageManager";

export const HowlCache = {};
export const soundDurations = {};

export const loadSounds = createAsyncThunk(
  "sounds/loadSounds",
  async (_, { rejectWithValue }) => {
    // 🚀 PARALLEL LOADING - Load all sounds simultaneously
    const soundPromises = GAME_SOUNDS.sounds.map(async (sound) => {
      if (!HowlCache[sound.name]) {
        // Create Howl with sprite support
        const howlConfig = {
          src: [sound.url],
          loop: sound.loop || false,
          volume: sound.volume || 1.0,
        };

        // Add sprite configuration if present
        if (sound.sprite) {
          howlConfig.sprite = sound.sprite;
        }

        HowlCache[sound.name] = new Howl(howlConfig);

        try {
          await new Promise((resolve, reject) => {
            const howl = HowlCache[sound.name];

            // Add more comprehensive error handling
            howl.once("load", () => {
              soundDurations[sound.name] = howl.duration();

              // If this is a sprite, also store individual sprite durations
              if (sound.sprite) {
                Object.entries(sound.sprite).forEach(([spriteName, [start, duration]]) => {
                  soundDurations[spriteName] = duration / 1000; // Convert ms to seconds
                });
              }

              resolve();
            });

            howl.once("loaderror", (id, error) => {
              reject(
                new Error(`Failed to load sound: ${sound.name} - ${error}`)
              );
            });
          });
        } catch (error) {
          throw error; // Let Promise.all catch this
        }
      }
    });

    // Wait for all sounds to finish loading (happens in parallel!)
    try {
      await Promise.all(soundPromises);
    } catch (error) {
      return rejectWithValue(`Failed to load some sounds`);
    }

    return {
      soundKeys: Object.keys(HowlCache),
      durations: soundDurations,
    };
  }
);

// Load saved sound settings from localStorage
const savedSettings = StorageManager.loadSoundSettings();

const saveSoundSettings = (state) => {
  StorageManager.saveSoundSettings({
    masterVolume: state.masterVolume,
    soundEffectsEnabled: state.soundEffectsEnabled,
    backgroundMusicEnabled: state.backgroundMusicEnabled,
    globalSoundDisabled: state.globalSoundDisabled,
  });
};

/**
 * Enter global mute: volume bar → 0, both checkboxes off, all audio stopped.
 * Remembers volume for restore. Sets previous* checkboxes to BOTH on so that
 * dragging volume back up re-checks Music + Sound FX (requirement).
 */
const enterGlobalMuteFromCheckboxes = (state) => {
  if (state.masterVolume > 0) {
    state.previousMasterVolume = state.masterVolume;
  }
  state.previousSoundEffectsEnabled = true;
  state.previousBackgroundMusicEnabled = true;

  state.masterVolume = 0;
  state.globalSoundDisabled = true;
  state.soundEffectsEnabled = false;
  state.backgroundMusicEnabled = false;

  Object.values(HowlCache).forEach((sound) => {
    sound.mute(true);
    if (sound.playing()) sound.stop();
  });

  soundManager.toggleGlobal(true);
  soundManager.soundEffectsEnabled = false;
  soundManager.backgroundMusicEnabled = false;
  soundManager.globalSoundDisabled = true;
};

const soundsSlice = createSlice({
  name: "sounds",
  initialState: {
    soundEffectsEnabled: savedSettings.soundEffectsEnabled,
    backgroundMusicEnabled: savedSettings.backgroundMusicEnabled,
    globalSoundDisabled: savedSettings.globalSoundDisabled,
    masterVolume: savedSettings.masterVolume,
    previousMasterVolume: savedSettings.masterVolume, // Initialize with saved volume
    previousSoundEffectsEnabled: savedSettings.soundEffectsEnabled, // Initialize with saved state
    previousBackgroundMusicEnabled: savedSettings.backgroundMusicEnabled, // Initialize with saved state
    currentBackgroundMusic: SOUND_NAME.background_music,
    soundKeys: [],
    soundVolumes: {},
    status: "idle",
    error: null,
  },
  reducers: {
    toggleSoundEffects(state) {
      state.soundEffectsEnabled = !state.soundEffectsEnabled;

      // If re-enabling, clear globalSoundDisabled (we have sound again)
      if (state.soundEffectsEnabled && state.globalSoundDisabled) {
        state.globalSoundDisabled = false;
        state.masterVolume = state.previousMasterVolume > 0 ? state.previousMasterVolume : 0.7;

        // Restore global master volume
        Howler.volume(state.masterVolume);

        soundManager.toggleGlobal(false);
      }

      // Both channels off via checkboxes → same as global mute / volume at 0
      if (!state.soundEffectsEnabled && !state.backgroundMusicEnabled) {
        enterGlobalMuteFromCheckboxes(state);
        saveSoundSettings(state);
        return;
      }

      // Build list of music tracks to exclude from sound effects toggle
      const musicTracks = [
        SOUND_NAME.background_music,
        SOUND_NAME.free_spin_background_music,
        SOUND_NAME.feature_background_music,
        SOUND_NAME.total_win_loop,
        SOUND_NAME.max_win_loop,
        SOUND_NAME.win_music,
      ];
      if (SOUND_NAME.ambient_music) {
        musicTracks.push(SOUND_NAME.ambient_music);
      }
      if (SOUND_NAME.feature_ambient_music) {
        musicTracks.push(SOUND_NAME.feature_ambient_music);
      }

      // Mute/unmute only non-music sounds
      Object.entries(HowlCache).forEach(([key, sound]) => {
        if (!musicTracks.includes(key)) {
          sound.mute(!state.soundEffectsEnabled);
        }
      });

      // Sync SoundManager
      soundManager.soundEffectsEnabled = state.soundEffectsEnabled;
      soundManager.globalSoundDisabled = state.globalSoundDisabled;

      saveSoundSettings(state);
    },

    toggleBackgroundMusic(state) {
      state.backgroundMusicEnabled = !state.backgroundMusicEnabled;

      // If re-enabling, clear globalSoundDisabled (we have sound again)
      if (state.backgroundMusicEnabled && state.globalSoundDisabled) {
        state.globalSoundDisabled = false;
        state.masterVolume = state.previousMasterVolume > 0 ? state.previousMasterVolume : 0.7;

        // Restore global master volume
        Howler.volume(state.masterVolume);

        soundManager.toggleGlobal(false);
      }

      // Both channels off via checkboxes → same as global mute / volume at 0
      if (!state.soundEffectsEnabled && !state.backgroundMusicEnabled) {
        enterGlobalMuteFromCheckboxes(state);
        saveSoundSettings(state);
        return;
      }

      // Sync SoundManager FIRST so play() sees the correct state
      soundManager.backgroundMusicEnabled = state.backgroundMusicEnabled;
      soundManager.globalSoundDisabled = state.globalSoundDisabled;

      // Handle music tracks
      const regularMusic = HowlCache[SOUND_NAME.background_music];
      const freeSpinMusic = HowlCache[SOUND_NAME.free_spin_background_music];
      const featureMusic = HowlCache[SOUND_NAME.feature_background_music];

      if (state.backgroundMusicEnabled) {
        // Use soundManager.currentBackgroundMusic as source of truth
        // It's kept in sync by scene transitions (base game, free spins, H&W)
        const musicToPlay = soundManager.currentBackgroundMusic || SOUND_NAME.background_music;
        state.currentBackgroundMusic = musicToPlay;

        // Only start playback if not deferred (intro popup still showing)
        if (!soundManager.musicPlaybackDeferred) {
          soundManager.play(musicToPlay);
        }
      } else {
        // Stop all music tracks (SoundManager.toggleBackgroundMusic handles ambient)
        if (regularMusic) regularMusic.stop();
        if (freeSpinMusic) freeSpinMusic.stop();
        if (featureMusic) featureMusic.stop();
        soundManager.toggleBackgroundMusic(false);
      }

      saveSoundSettings(state);
    },

    toggleGlobalSound(state, action) {
      state.globalSoundDisabled = !state.globalSoundDisabled;

      // CRITICAL: Sync SoundManager FIRST before manipulating HowlCache
      // This ensures soundManager.play() checks use the correct state immediately
      soundManager.toggleGlobal(state.globalSoundDisabled);

      if (state.globalSoundDisabled) {
        // Store current states before disabling
        state.previousMasterVolume = state.masterVolume;
        state.previousSoundEffectsEnabled = state.soundEffectsEnabled;
        state.previousBackgroundMusicEnabled = state.backgroundMusicEnabled;

        // Set volume to 0
        state.masterVolume = 0;

        state.soundEffectsEnabled = false;
        state.backgroundMusicEnabled = false;

        // Mute and stop all sounds — do NOT set sound.volume(0) as that
        // poisons the per-Howl instance volume which isn't restored on re-enable
        Object.values(HowlCache).forEach((sound) => {
          sound.mute(true);
          if (sound.playing()) sound.stop();
        });
      } else {
        // When re-enabling from global mute, enable both checkboxes
        // This is the expected behavior when clicking the sound button to unmute
        state.soundEffectsEnabled = true;
        state.backgroundMusicEnabled = true;

        // Restore volume to previous value, or default to 1.0 if it was 0
        state.masterVolume = state.previousMasterVolume > 0 ? state.previousMasterVolume : 0.7;

        // Apply restored volume and unmute all sounds
        Howler.volume(state.masterVolume);
        Object.entries(HowlCache).forEach(([name, sound]) => {
          sound.mute(false);
          // Restore per-sound volume from config (undoes any volume(0) from prior disable)
          const vol = soundManager._getSoundVolume(name);
          sound.volume(vol);
        });

        if (state.backgroundMusicEnabled && !soundManager.musicPlaybackDeferred) {
          const musicToPlay = soundManager.currentBackgroundMusic || SOUND_NAME.background_music;
          state.currentBackgroundMusic = musicToPlay;
          soundManager.play(musicToPlay);
        }
      }

      saveSoundSettings(state);
    },

    setMasterVolume(state, action) {
      const newVolume = Math.max(0, Math.min(2, action.payload));
      const wasZero = state.masterVolume === 0;
      const isNowZero = newVolume === 0;

      // Moving TO zero - disable all sounds
      if (!wasZero && isNowZero) {
        // Store current checkbox states before disabling
        state.previousSoundEffectsEnabled = state.soundEffectsEnabled;
        state.previousBackgroundMusicEnabled = state.backgroundMusicEnabled;
        state.previousMasterVolume = state.masterVolume > 0 ? state.masterVolume : 0.5;

        // Disable everything
        state.globalSoundDisabled = true;
        state.soundEffectsEnabled = false;
        state.backgroundMusicEnabled = false;
        state.masterVolume = 0;

        // Mute and stop all sounds — do NOT set sound.volume(0) as that
        // poisons the per-Howl instance volume which isn't restored on re-enable
        Object.values(HowlCache).forEach((sound) => {
          sound.mute(true);
          if (sound.playing()) sound.stop();
        });

        // Sync SoundManager
        soundManager.toggleGlobal(true);
        soundManager.soundEffectsEnabled = false;
        soundManager.backgroundMusicEnabled = false;
        soundManager.globalSoundDisabled = true;

        saveSoundSettings(state);
        return;
      }

      // Moving FROM zero - restore previous checkbox states
      if (wasZero && !isNowZero) {
        state.masterVolume = newVolume;
        state.globalSoundDisabled = false;

        // If both were off (e.g. user unchecked both, then dragged volume),
        // re-check BOTH. Otherwise restore whichever was on before volume→0.
        if (
          !state.previousSoundEffectsEnabled &&
          !state.previousBackgroundMusicEnabled
        ) {
          state.soundEffectsEnabled = true;
          state.backgroundMusicEnabled = true;
        } else {
          state.soundEffectsEnabled = state.previousSoundEffectsEnabled;
          state.backgroundMusicEnabled = state.previousBackgroundMusicEnabled;
        }

        // Apply volume and unmute all sounds, restoring per-sound volumes
        Howler.volume(state.masterVolume);
        Object.entries(HowlCache).forEach(([name, sound]) => {
          sound.mute(false);
          const vol = soundManager._getSoundVolume(name);
          sound.volume(vol);
        });

        if (state.backgroundMusicEnabled && !soundManager.musicPlaybackDeferred) {
          const musicToPlay = soundManager.currentBackgroundMusic || SOUND_NAME.background_music;
          state.currentBackgroundMusic = musicToPlay;
          soundManager.play(musicToPlay);
        }

        // Sync SoundManager
        soundManager.toggleGlobal(false);
        soundManager.soundEffectsEnabled = state.soundEffectsEnabled;
        soundManager.backgroundMusicEnabled = state.backgroundMusicEnabled;
        soundManager.globalSoundDisabled = false;

        saveSoundSettings(state);
        return;
      }

      // Normal volume change (not crossing zero threshold)
      state.masterVolume = newVolume;

      // Apply volume globally
      Howler.volume(state.masterVolume);

      saveSoundSettings(state);
    },

    // Reset all sound settings to defaults (called on new player session)
    resetSoundSettings(state) {
      const defaults = StorageManager.loadSoundSettings(); // localStorage already cleared, returns defaults
      state.soundEffectsEnabled = defaults.soundEffectsEnabled;
      state.backgroundMusicEnabled = defaults.backgroundMusicEnabled;
      state.globalSoundDisabled = defaults.globalSoundDisabled;
      state.masterVolume = defaults.masterVolume;
      state.previousMasterVolume = defaults.masterVolume;
      state.previousSoundEffectsEnabled = defaults.soundEffectsEnabled;
      state.previousBackgroundMusicEnabled = defaults.backgroundMusicEnabled;

      // Sync runtime
      Howler.volume(defaults.masterVolume);
      soundManager.globalSoundDisabled = defaults.globalSoundDisabled;
      soundManager.soundEffectsEnabled = defaults.soundEffectsEnabled;
      soundManager.backgroundMusicEnabled = defaults.backgroundMusicEnabled;
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(loadSounds.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(loadSounds.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.soundKeys = action.payload.soundKeys;
        state.soundDurations = {
          ...state.soundDurations,
          ...action.payload.durations,
        };

        // Apply saved settings to all loaded sounds
        if (state.globalSoundDisabled) {
          // Global sound disabled - mute everything and set volume to 0
          Object.values(HowlCache).forEach((sound) => {
            sound.volume(0);
            sound.mute(true);
          });
        } else {
          // Apply saved master volume
          Howler.volume(state.masterVolume);

          // Apply mute state based on individual settings
          Object.entries(HowlCache).forEach(([key, sound]) => {
            // Build list of music tracks
            const musicTracks = [
              SOUND_NAME.background_music,
              SOUND_NAME.feature_background_music,
              SOUND_NAME.free_spin_background_music,
              SOUND_NAME.total_win_loop,
              SOUND_NAME.max_win_loop,
              SOUND_NAME.win_music,
            ];
            
            if (SOUND_NAME.ambient_music) {
              musicTracks.push(SOUND_NAME.ambient_music);
            }
            if (SOUND_NAME.feature_ambient_music) {
              musicTracks.push(SOUND_NAME.feature_ambient_music);
            }
            
            // Music sounds
            if (musicTracks.includes(key)) {
              sound.mute(!state.backgroundMusicEnabled);
            }
            // Sound effects (everything else)
            else {
              sound.mute(!state.soundEffectsEnabled);
            }
          });
        }
      })
      .addCase(loadSounds.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
      });
  },
});

export const {
  toggleSoundEffects,
  toggleBackgroundMusic,
  toggleGlobalSound,
  setMasterVolume,
  resetSoundSettings,
} = soundsSlice.actions;

export const selectMasterVolume = (state) => state.sound.masterVolume;

export default soundsSlice.reducer;
